import { useEffect, useId, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Overlay, Tooltip } from "react-bootstrap";
import "../../styles/tooltips.css";

const SELECTOR = "[data-hint]";
const OPEN_CONTROL = '[aria-expanded="true"]';
const placements = new Set(["top", "bottom", "left", "right", "auto", "top-start", "top-end", "bottom-start", "bottom-end", "left-start", "left-end", "right-start", "right-end"]);

function hintTarget(node) {
  const target = node instanceof Element ? node.closest(SELECTOR) : null;
  if (!target?.getAttribute("data-hint")?.trim()) return null;
  if (!target.isConnected || !target.getClientRects().length || target.closest('[aria-hidden="true"], [inert]')) return null;
  // A click-open menu/popover owns the interaction until it closes.
  if (target.matches(OPEN_CONTROL) || target.querySelector(OPEN_CONTROL)) return null;
  return target;
}

/** One Bootstrap tooltip for all plain-text hints, including large job lists.
 * Event delegation avoids a React overlay and listeners on every table cell.
 * Authors use data-hint or Hint; no scanning or rewriting of native titles. */
export default function AppTooltips() {
  const [hint, setHint] = useState(null);
  const active = useRef(null);
  const dismissRef = useRef(() => {});
  const id = `app-hint-${useId()}`;
  const location = useLocation();

  useEffect(() => {
    let hovered = null, focused = null, dismissed = null, timer, pointerPaused = false;
    const clearTimer = () => window.clearTimeout(timer);
    const hide = () => {
      clearTimer();
      active.current = null;
      setHint(null);
    };
    const dismiss = () => {
      pointerPaused = true;
      dismissed = hovered || focused || active.current?.target;
      hide();
    };
    dismissRef.current = dismiss;
    const show = (target) => {
      if (hintTarget(target) !== target || dismissed === target) return hide();
      const requested = target.getAttribute("data-hint-placement") || "top";
      const next = {
        target,
        label: target.getAttribute("data-hint"),
        placement: placements.has(requested) ? requested : "top",
        // Keep tooltips inside modal/offcanvas stacking contexts and focus traps.
        container: target.closest('.modal, .offcanvas, [role="dialog"]') || document.body,
      };
      active.current = next;
      setHint(next);
    };
    const update = () => {
      clearTimer();
      const target = hovered || focused;
      if (!target || target === dismissed) {
        timer = window.setTimeout(hide, 100);
        return;
      }
      if (active.current?.target === target) {
        if (target === focused) show(target); // Describe the newly focused child.
        return;
      }
      hide();
      if (target === focused) show(target);
      else timer = window.setTimeout(() => show(target), 220);
    };
    const over = (event) => {
      if (event.pointerType === "touch" || pointerPaused) return;
      // Users can move onto the tooltip to read a long hint without losing it.
      if (event.target instanceof Element && event.target.closest('.app-tooltip')) {
        clearTimer();
        return;
      }
      const target = hintTarget(event.target);
      if (target !== hovered) {
        dismissed = null;
        hovered = target;
        update();
      }
    };
    const out = (event) => {
      if (event.pointerType === "touch" || pointerPaused) return;
      if (event.relatedTarget instanceof Element && event.relatedTarget.closest('.app-tooltip')) {
        clearTimer();
        return;
      }
      const next = hintTarget(event.relatedTarget);
      if (next === hovered && !(event.target instanceof Element && event.target.closest('.app-tooltip'))) return;
      hovered = next;
      dismissed = null;
      update();
    };
    const focusIn = (event) => {
      const candidate = hintTarget(event.target);
      const target = candidate?.getAttribute("data-hint-trigger") === "hover" ? null : candidate;
      if (target !== focused || hovered) {
        if (target !== dismissed) dismissed = null;
        focused = target;
        hovered = null; // Keyboard focus takes precedence over a parked mouse.
        update();
      }
    };
    const focusOut = (event) => {
      const candidate = hintTarget(event.relatedTarget);
      const next = candidate?.getAttribute("data-hint-trigger") === "hover" ? null : candidate;
      if (next === focused) return;
      focused = next;
      dismissed = null;
      update();
    };
    const pointerMove = (event) => {
      if (!pointerPaused || event.pointerType === "touch") return;
      // Dragging a splitter/row must not re-open its hint under the pointer.
      if (event.buttons) return;
      // Removing an overlay can expose a different hint beneath a stationary
      // pointer. After dismissal, wait for a real move before showing another.
      pointerPaused = false;
      dismissed = null;
      hovered = hintTarget(event.target);
      update();
    };
    const pointerDown = (event) => {
      pointerPaused = true;
      dismissed = hintTarget(event.target) || hovered || focused;
      hide();
    };
    const keyDown = (event) => {
      if (["Escape", "Enter", " ", "ArrowDown", "ArrowUp"].includes(event.key)) dismiss();
      // No preventDefault: the button, dropdown or popover handles its own key.
    };
    const events = [
      [document, "pointerover", over], [document, "pointerout", out],
      [document, "focusin", focusIn], [document, "focusout", focusOut],
      [document, "pointerdown", pointerDown], [document, "pointermove", pointerMove],
      [document, "keydown", keyDown],
      [window, "scroll", dismiss], [window, "resize", dismiss],
      [window, "blur", dismiss], [window, "beforeprint", dismiss],
      [window, "claimsdesk:theme-changed", dismiss],
    ];
    events.forEach(([node, name, handler]) => node.addEventListener(name, handler, true));
    return () => {
      clearTimer();
      events.forEach(([node, name, handler]) => node.removeEventListener(name, handler, true));
      active.current = null;
    };
  }, []);

  useEffect(() => { dismissRef.current(); }, [location.pathname, location.search]);

  useEffect(() => {
    if (!hint) return;
    const { target } = hint;
    const described = target.contains(document.activeElement) ? document.activeElement : target;
    const descriptions = new Set((described.getAttribute("aria-describedby") || "").split(/\s+/).filter(Boolean));
    descriptions.add(id);
    described.setAttribute("aria-describedby", [...descriptions].join(" "));
    // Observe only while visible: remove stale hints after filtering/removal,
    // update changing text, and yield as soon as a menu opens.
    const observer = new MutationObserver((records) => {
      if (!hintTarget(target)) return dismissRef.current();
      if (!records.some((r) => r.target === target)) return;
      const label = target.getAttribute("data-hint");
      if (label !== active.current?.label) {
        const next = { ...hint, label };
        active.current = next;
        setHint(next);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["data-hint", "aria-expanded"] });
    return () => {
      observer.disconnect();
      const remaining = (described.getAttribute("aria-describedby") || "").split(/\s+/).filter((value) => value && value !== id);
      if (remaining.length) described.setAttribute("aria-describedby", remaining.join(" "));
      else described.removeAttribute("aria-describedby");
    };
  }, [hint, id]);

  return hint ? (
    <Overlay show target={hint.target} placement={hint.placement} container={hint.container} flip transition={false}>
      <Tooltip id={id} className="app-tooltip no-print">{hint.label}</Tooltip>
    </Overlay>
  ) : null;
}
